import dayjs from 'dayjs';

// 處理時間格式
export const formatDateTime = (dateTime) => {
    if (!dateTime) return undefined;
    if (typeof dateTime === 'string') return dateTime;
    if (dateTime.format) {
        return dateTime.format('YYYY-MM-DD HH:mm:ss');
    }
    return undefined;
};

// 處理時間欄位（格式化並移除空值）
export const processTimeFields = (submitData, deleteEmpty = false) => {
    if (submitData.start_time) {
        submitData.start_time = formatDateTime(submitData.start_time);
    } else if (deleteEmpty) {
        delete submitData.start_time;
    }

    if (submitData.end_time) {
        submitData.end_time = formatDateTime(submitData.end_time);
    } else if (deleteEmpty) {
        delete submitData.end_time;
    }
};

// 處理 rewards：過濾掉空的獎勵項目，並轉換為 API 格式
export const processRewards = (rewards) => {
    if (!rewards || !Array.isArray(rewards)) return [];

    return rewards
        .filter((reward) => {
            if (reward.reward_type === 'exp') {
                return reward.value !== undefined && reward.value !== null;
            }
            return (
                reward.reference_id !== undefined &&
                reward.reference_id !== null &&
                reward.value !== undefined &&
                reward.value !== null
            );
        })
        .map((reward) => {
            const result = {
                reward_type: reward.reward_type || 'point',
                value: reward.value
            };

            // 只有點數類型才需要 reference_id
            if (reward.reward_type === 'point') {
                result.reference_id = reward.reference_id;
            }

            return result;
        });
};

// 處理 executable_rules：過濾掉空的規則，並處理多選
export const processExecutableRules = (executableRules) => {
    if (!executableRules || !Array.isArray(executableRules)) return [];

    return executableRules
        .filter((rule) => {
            if (!rule.actor_type) return false;
            return Array.isArray(rule.actor_ref) && rule.actor_ref.length > 0;
        })
        .map((rule) => {
            if (Array.isArray(rule.actor_ref)) {
                return {
                    ...rule,
                    actor_ref: rule.actor_ref.join(',')
                };
            }
            return rule;
        });
};

// 處理 visibility_rules：過濾掉空的規則，並處理多選
export const processVisibilityRules = (visibilityRules) => {
    if (!visibilityRules || !Array.isArray(visibilityRules)) return [];

    return visibilityRules
        .filter((rule) => {
            if (!rule.audience_type) return false;
            return (
                Array.isArray(rule.audience_ref) && rule.audience_ref.length > 0
            );
        })
        .map((rule) => {
            if (Array.isArray(rule.audience_ref)) {
                return {
                    ...rule,
                    audience_ref: rule.audience_ref.join(',')
                };
            }
            return rule;
        });
};

// 移除 undefined 值
export const removeUndefinedValues = (obj) => {
    Object.keys(obj).forEach((key) => {
        if (obj[key] === undefined) {
            delete obj[key];
        }
    });
};

/**
 * 統一處理提交資料格式
 * @param {Object} formData - 表單資料
 * @param {Object} options - 選項
 * @param {Array<string>} options.fields - 要包含的欄位列表（如果提供，只會包含這些欄位）
 * @param {Object} options.fieldDefaults - 欄位預設值（用於將 undefined 轉為 null）
 * @param {boolean} options.deleteEmptyTime - 是否刪除空的時間欄位
 * @param {boolean} options.processConditions - 是否處理條件
 * @returns {Object} 處理後的提交資料
 */
export const processSubmitData = (formData, options = {}) => {
    const {
        fields = null,
        fieldDefaults = {},
        deleteEmptyTime = false,
        processConditions = false
    } = options;

    // 如果指定了欄位列表，只包含這些欄位
    let submitData = fields
        ? fields.reduce((acc, field) => {
              acc[field] = formData[field];
              return acc;
          }, {})
        : { ...formData };

    // 處理時間格式
    processTimeFields(submitData, deleteEmptyTime);

    // 處理獎勵
    if (submitData.rewards !== undefined) {
        submitData.rewards = processRewards(formData.rewards);
    }

    // 處理可見性規則
    if (submitData.visibility_rules !== undefined) {
        submitData.visibility_rules = processVisibilityRules(
            formData.visibility_rules
        );
    }

    // 處理可執行規則
    if (submitData.executable_rules !== undefined) {
        submitData.executable_rules = processExecutableRules(
            formData.executable_rules
        );
    }

    // 處理條件（如果需要）
    if (processConditions && submitData.conditions !== undefined) {
        submitData.conditions = formData.conditions
            .filter((condition) => condition.type)
            .map((condition) => {
                const result = {
                    type: condition.type
                };
                if (condition.code) {
                    result.code = condition.code;
                }
                if (condition.operator) {
                    result.operator = condition.operator;
                }
                if (condition.required_value !== undefined) {
                    result.required_value = condition.required_value;
                }
                if (condition.require_review !== undefined) {
                    result.require_review = condition.require_review;
                }
                if (condition.redirect_url) {
                    result.redirect_url = condition.redirect_url;
                }
                if (condition.node_id) {
                    result.node_id = condition.node_id;
                }
                return result;
            });
    }

    // 處理欄位預設值（將 undefined 轉為 null）
    Object.keys(fieldDefaults).forEach((field) => {
        if (submitData[field] === undefined) {
            submitData[field] = fieldDefaults[field];
        }
    });

    // 移除 undefined 值
    removeUndefinedValues(submitData);

    return submitData;
};
